// File: frontend/src/pages/students/StudentDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api, { getImageUrl } from '../../utils/axios';
import PageHeader from '../../components/PageHeader';

const StudentDetail = () => {
  const { school } = useSchool();
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Active View Tab: 'profile' | 'receipt' | 'tc'
  const [activeTab, setActiveTab] = useState('profile');

  // Modals for Actions
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteClass, setPromoteClass] = useState('');
  const [promoteSection, setPromoteSection] = useState('');
  const [promoteLoading, setPromoteLoading] = useState(false);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferReason, setTransferReason] = useState('');
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
  const [transferLoading, setTransferLoading] = useState(false);

  const [receiptData, setReceiptData] = useState(null);
  const [tcData, setTcData] = useState(null);
  const [docLoading, setDocLoading] = useState(false);

  /**
   * Fetch student details by ID
   */
  const fetchStudent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/students/${id}`);
      if (response.data?.success) {
        setStudent(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load student details.');
      }
    } catch (err) {
      console.error('Error fetching student details:', err);
      setError(err.response?.data?.message || err.message || 'Error fetching student details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Fetch admission receipt payload
   */
  const handleLoadReceipt = async () => {
    try {
      setDocLoading(true);
      const response = await api.get(`/api/v1/students/${id}/receipt`);
      if (response.data?.success) {
        setReceiptData(response.data.data);
        setActiveTab('receipt');
      }
    } catch (err) {
      console.error('Error loading receipt:', err);
      alert('Failed to load admission receipt');
    } finally {
      setDocLoading(false);
    }
  };

  /**
   * Fetch Transfer Certificate payload
   */
  const handleLoadTC = async () => {
    try {
      setDocLoading(true);
      const response = await api.get(`/api/v1/students/${id}/tc`);
      if (response.data?.success) {
        setTcData(response.data.data);
        setActiveTab('tc');
      }
    } catch (err) {
      console.error('Error loading TC:', err);
      alert('Failed to load Transfer Certificate');
    } finally {
      setDocLoading(false);
    }
  };

  /**
   * Promote student action API trigger
   */
  const handlePromoteSubmit = async (e) => {
    e.preventDefault();
    if (!promoteClass) {
      alert('Please select a target class');
      return;
    }

    try {
      setPromoteLoading(true);
      const response = await api.post(`/api/v1/students/${id}/promote`, {
        new_class_id: promoteClass,
        section: promoteSection
      });

      if (response.data?.success) {
        alert(response.data.message || 'Student promoted successfully!');
        setShowPromoteModal(false);
        fetchStudent();
      }
    } catch (err) {
      console.error('Error promoting student:', err);
      alert(err.response?.data?.message || 'Failed to promote student');
    } finally {
      setPromoteLoading(false);
    }
  };

  /**
   * Transfer student action API trigger
   */
  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    try {
      setTransferLoading(true);
      const response = await api.post(`/api/v1/students/${id}/transfer`, {
        reason_for_transfer: transferReason,
        date_of_leaving: transferDate
      });

      if (response.data?.success) {
        alert('Student marked as transferred!');
        setShowTransferModal(false);
        fetchStudent();
      }
    } catch (err) {
      console.error('Error transferring student:', err);
      alert(err.response?.data?.message || 'Failed to transfer student');
    } finally {
      setTransferLoading(false);
    }
  };

  /**
   * Deactivate student action API trigger
   */
  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate (soft delete) this student?')) return;
    try {
      const response = await api.delete(`/api/v1/students/${id}`);
      if (response.data?.success) {
        alert('Student deactivated successfully');
        fetchStudent();
      }
    } catch (err) {
      console.error('Error deactivating student:', err);
      alert(err.response?.data?.message || 'Failed to deactivate student');
    }
  };

  /**
   * Directly upload student photo from detail page
   */
  const handleDirectPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please select a valid image file (.jpg, .jpeg, .png only).');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Photo file size must not exceed 2MB.');
      return;
    }

    try {
      const photoFormData = new FormData();
      photoFormData.append('photo', file);
      const res = await api.post(`/api/v1/students/${id}/photo`, photoFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        fetchStudent();
      }
    } catch (err) {
      console.error('Error uploading student photo:', err);
      alert(err.response?.data?.message || 'Failed to upload photo');
    }
  };

  /**
   * Helper function for status badge style
   */
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'inactive':
        return 'bg-slate-100 text-slate-700 border border-slate-200';
      case 'transferred':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'graduated':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="flex items-center gap-3 text-slate-600 font-medium">
          <svg className="w-6 h-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading student profile...
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full border border-slate-200 shadow-sm text-center">
          <h2 className="text-xl font-bold text-slate-800 mb-2">Student Not Found</h2>
          <p className="text-sm text-slate-500 mb-6">{error || 'The requested student record does not exist.'}</p>
          <Link to="/students" className="px-6 py-2.5 bg-primary text-white font-medium rounded-xl text-sm shadow-sm">
            Return to Directory
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col print:bg-white print:p-0">
      
      {/* Printable CSS Rules */}
      <style>{`
        @media print {
          header, nav, button, .no-print {
            display: none !important;
          }
          body, main {
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>

      {/* Page Navigation Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <PageHeader
          title={student.student_name}
          subtitle={`Student ID: ${student.student_id} • Class: ${student.class_name || student.class_id || 'N/A'}`}
          breadcrumbs={[
            { label: 'Students', path: '/students' },
            { label: student.student_name }
          ]}
          backUrl="/students"
        >
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'profile'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Profile Overview
          </button>
          <button
            onClick={handleLoadReceipt}
            disabled={docLoading}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'receipt'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Admission Receipt 🧾
          </button>
          <button
            onClick={handleLoadTC}
            disabled={docLoading}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
              activeTab === 'tc'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Transfer Certificate 📜
          </button>
        </PageHeader>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 print:p-0 print:max-w-none">
        
        {/* Tab 1: Profile Card View */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            
            {/* Header Banner Card */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="relative group w-20 h-20 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center font-extrabold text-2xl shadow-md border-2 border-white overflow-hidden shrink-0">
                  {student.photo ? (
                    <img src={getImageUrl(student.photo)} alt={student.student_name} className="w-full h-full object-cover" />
                  ) : (
                    <span>{student.student_name ? student.student_name.slice(0, 2).toUpperCase() : 'ST'}</span>
                  )}
                  <label className="absolute inset-0 bg-black/60 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center cursor-pointer">
                    <span>📷 Upload</span>
                    <input
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      onChange={handleDirectPhotoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-2xl font-bold text-slate-900">{student.student_name}</h2>
                    <span className={`px-3 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${getStatusBadge(student.status)}`}>
                      {student.status || 'Active'}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-slate-600">
                    ID: <span className="font-mono text-primary font-bold">{student.student_id}</span> • Class {student.class_id} {student.section ? `(${student.section})` : ''} • Roll: {student.roll_no || 'N/A'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Enrolled: {student.join_date ? new Date(student.join_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 self-stretch sm:self-auto justify-end">
                <label className="px-3.5 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 font-medium rounded-xl text-xs transition cursor-pointer flex items-center gap-1">
                  <span>📷</span>
                  <span>{student.photo ? 'Change Photo' : 'Upload Photo'}</span>
                  <input
                    type="file"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={handleDirectPhotoUpload}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => navigate(`/students/edit/${student.id}`)}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-xs transition"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => setShowPromoteModal(true)}
                  className="px-3.5 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium rounded-xl text-xs transition"
                >
                  Promote
                </button>
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="px-3.5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium rounded-xl text-xs transition"
                >
                  Transfer
                </button>
                <button
                  onClick={handleDeactivate}
                  className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 font-medium rounded-xl text-xs transition"
                >
                  Deactivate
                </button>
              </div>
            </div>

            {/* Profile Grid Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Card 1: Personal Details */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider text-primary flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Personal Details
                </h3>
                <div className="space-y-2 text-xs divide-y divide-slate-100">
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Full Name</span><span className="font-semibold text-slate-800">{student.student_name}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Date of Birth</span><span className="font-semibold text-slate-800">{student.date_of_birth || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Gender</span><span className="font-semibold text-slate-800">{student.gender || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Religion</span><span className="font-semibold text-slate-800">{student.religion || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Blood Group</span><span className="font-semibold text-slate-800">{student.blood_group || 'N/A'}</span></div>
                </div>
              </div>

              {/* Card 2: Parent & Contact Info */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider text-indigo-600 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Parent & Contact
                </h3>
                <div className="space-y-2 text-xs divide-y divide-slate-100">
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Father Name</span><span className="font-semibold text-slate-800">{student.father_name}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Mother Name</span><span className="font-semibold text-slate-800">{student.mother_name || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Phone</span><span className="font-semibold text-slate-800">{student.phone}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Email</span><span className="font-semibold text-slate-800">{student.email || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Emergency</span><span className="font-semibold text-slate-800">{student.emergency_contact || 'N/A'}</span></div>
                </div>
              </div>

              {/* Card 3: Academic & Location */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wider text-emerald-600 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Academic & Location
                </h3>
                <div className="space-y-2 text-xs divide-y divide-slate-100">
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Current Class</span><span className="font-semibold text-slate-800">Class {student.class_id}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Section</span><span className="font-semibold text-slate-800">{student.section || 'A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">City</span><span className="font-semibold text-slate-800">{student.city || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Address</span><span className="font-semibold text-slate-800 truncate max-w-[150px]">{student.address || 'N/A'}</span></div>
                  <div className="pt-2 flex justify-between"><span className="text-slate-500">Status</span><span className="font-semibold text-slate-800 capitalize">{student.status}</span></div>
                </div>
              </div>

            </div>

          </div>
        )}

        {/* Tab 2: Admission Receipt Printable View */}
        {activeTab === 'receipt' && (
          <div className="bg-white rounded-2xl p-8 sm:p-12 border border-slate-200 shadow-md max-w-3xl mx-auto print-container">
            <div className="no-print flex justify-end mb-6">
              <button
                onClick={() => window.print()}
                className="px-6 py-2.5 bg-primary hover:bg-blue-600 text-white font-semibold rounded-xl text-sm shadow-sm transition flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Receipt (A4)
              </button>
            </div>

            {docLoading ? (
              <div className="py-12 text-center text-slate-500">Loading receipt details...</div>
            ) : (
              <div className="border-4 border-slate-900 p-8 space-y-6 font-serif">
                {/* Header */}
                <div className="text-center border-b-2 border-slate-900 pb-4 space-y-1">
                  <h1 className="text-2xl font-bold uppercase tracking-widest text-slate-900">
                    {receiptData?.school?.name || school?.name || 'School Portal'}
                  </h1>
                  <p className="text-xs text-slate-600 font-sans">{receiptData?.school?.address || school?.address || 'Official Campus'}</p>
                  <div className="mt-3 inline-block px-4 py-1 bg-slate-900 text-white font-sans text-xs font-bold uppercase tracking-wider">
                    Official Admission Receipt
                  </div>
                </div>

                {/* Metadata Row */}
                <div className="flex justify-between text-xs font-sans border-b pb-4">
                  <div><strong>Receipt No:</strong> {receiptData?.receipt_no || `REC-2026-${student.id}`}</div>
                  <div><strong>Issue Date:</strong> {receiptData?.issue_date || new Date().toISOString().split('T')[0]}</div>
                </div>

                {/* Details Table */}
                <div className="grid grid-cols-2 gap-4 text-xs font-sans">
                  <div><strong>Student ID:</strong> {student.student_id}</div>
                  <div><strong>Full Name:</strong> {student.student_name}</div>
                  <div><strong>Father Name:</strong> {student.father_name}</div>
                  <div><strong>Class Admitted:</strong> Class {student.class_id}</div>
                  <div><strong>Admission Date:</strong> {student.join_date || 'N/A'}</div>
                  <div><strong>Contact Phone:</strong> {student.phone}</div>
                </div>

                {/* Statement */}
                <div className="pt-4 border-t text-xs italic text-slate-700 font-sans">
                  This receipt certifies that <strong>{student.student_name}</strong> has been officially enrolled into <strong>Class {student.class_id}</strong> for the academic session.
                </div>

                {/* Signatures */}
                <div className="pt-12 flex justify-between items-end text-xs font-sans">
                  <div className="text-center border-t border-slate-400 pt-2 px-6">
                    Accounts Manager
                  </div>
                  <div className="text-center border-t border-slate-900 pt-2 px-8 font-bold">
                    Principal / Authorized Signature
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Transfer Certificate (TC) Printable View */}
        {activeTab === 'tc' && (
          <div className="bg-white rounded-2xl p-8 sm:p-12 border border-slate-200 shadow-md max-w-3xl mx-auto print-container">
            <div className="no-print flex justify-end mb-6">
              <button
                onClick={() => window.print()}
                className="px-6 py-2.5 bg-primary hover:bg-blue-600 text-white font-semibold rounded-xl text-sm shadow-sm transition flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print Transfer Certificate (A4)
              </button>
            </div>

            {docLoading ? (
              <div className="py-12 text-center text-slate-500">Loading certificate details...</div>
            ) : (
              <div className="border-4 border-amber-900 p-8 space-y-6 font-serif bg-amber-50/20">
                {/* Header */}
                <div className="text-center border-b-2 border-amber-900 pb-4 space-y-1">
                  {school?.logo && (
                    <img src={school.logo} alt="Logo" className="w-14 h-14 object-contain mx-auto mb-2" />
                  )}
                  <h1 className="text-2xl font-bold uppercase tracking-widest text-amber-950">
                    {school?.name || tcData?.school?.name || 'School Portal'}
                  </h1>
                  <p className="text-xs text-slate-600 font-sans">{school?.address || tcData?.school?.address || 'Official Campus'}</p>
                  <p className="text-xs text-slate-500 font-sans font-bold">Ph: {school?.phone || '+1 (555) 019-2834'} | Affiliation No: {tcData?.school?.affiliation_no || 'AFF-9948201'}</p>
                  <div className="mt-3 inline-block px-6 py-1.5 bg-amber-900 text-white font-sans text-xs font-bold uppercase tracking-widest rounded-md">
                    TRANSFER CERTIFICATE
                  </div>
                </div>

                {/* Certificate Metadata */}
                <div className="flex justify-between text-xs font-sans border-b pb-4">
                  <div><strong>TC No:</strong> {tcData?.tc_no || `TC-2026-00${student.id}`}</div>
                  <div><strong>Date of Issue:</strong> {tcData?.issue_date || new Date().toISOString().split('T')[0]}</div>
                </div>

                {/* Body details */}
                <div className="space-y-3 text-xs font-sans leading-relaxed text-slate-800">
                  <p>1. Name of Student: <strong className="uppercase">{student.student_name}</strong></p>
                  <p>2. Father / Guardian Name: <strong>{student.father_name}</strong></p>
                  <p>3. Mother Name: <strong>{student.mother_name || 'N/A'}</strong></p>
                  <p>4. Date of Birth: <strong>{student.date_of_birth || 'N/A'}</strong></p>
                  <p>5. Date of Admission: <strong>{student.join_date || 'N/A'}</strong></p>
                  <p>6. Class at Time of Leaving: <strong>Class {student.class_id}</strong></p>
                  <p>7. Date of Leaving School: <strong>{student.date_of_leaving || new Date().toISOString().split('T')[0]}</strong></p>
                  <p>8. Reason for Leaving: <strong>{student.reason_for_transfer || 'Family Relocation / Higher Studies'}</strong></p>
                  <p>9. General Conduct: <strong>Good</strong></p>
                </div>

                {/* Signatures */}
                <div className="pt-16 flex justify-between items-end text-xs font-sans">
                  <div className="text-center border-t border-slate-400 pt-2 px-6">
                    Class Teacher Signature
                  </div>
                  <div className="text-center border-t border-amber-900 pt-2 px-8 font-bold flex flex-col items-center">
                    <div className="h-10 flex items-end justify-center gap-3 mb-1">
                      {school?.stamp && (
                        <img src={school.stamp} alt="Stamp" className="h-10 object-contain opacity-80" />
                      )}
                      {school?.principal_signature && (
                        <img src={school.principal_signature} alt="Signature" className="h-10 object-contain" />
                      )}
                    </div>
                    <span>{school?.principal_name || 'Principal'} Signature & Seal</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </main>

      {/* Modal 1: Promote Student */}
      {showPromoteModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Promote Student</h3>
              <button onClick={() => setShowPromoteModal(false)} className="font-bold text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handlePromoteSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Select Target Class <span className="text-red-500">*</span>
                </label>
                <select
                  value={promoteClass}
                  onChange={(e) => setPromoteClass(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">-- Choose Class --</option>
                  <option value="1">Class 1</option>
                  <option value="2">Class 2</option>
                  <option value="3">Class 3</option>
                  <option value="4">Class 4</option>
                  <option value="5">Class 5</option>
                  <option value="6">Class 6</option>
                  <option value="7">Class 7</option>
                  <option value="8">Class 8</option>
                  <option value="9">Class 9</option>
                  <option value="10">Class 10</option>
                  <option value="11">Class 11</option>
                  <option value="12">Class 12</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Section</label>
                <input
                  type="text"
                  placeholder="e.g. A"
                  value={promoteSection}
                  onChange={(e) => setPromoteSection(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowPromoteModal(false)} className="px-4 py-2 bg-slate-100 rounded-xl text-sm font-medium">Cancel</button>
                <button type="submit" disabled={promoteLoading} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium shadow-sm">
                  {promoteLoading ? 'Promoting...' : 'Confirm Promotion'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal 2: Transfer Student */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Transfer Student</h3>
              <button onClick={() => setShowTransferModal(false)} className="font-bold text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <form onSubmit={handleTransferSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Date of Leaving</label>
                <input
                  type="date"
                  value={transferDate}
                  onChange={(e) => setTransferDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Reason for Transfer</label>
                <textarea
                  rows="3"
                  placeholder="e.g. Relocation to another city..."
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowTransferModal(false)} className="px-4 py-2 bg-slate-100 rounded-xl text-sm font-medium">Cancel</button>
                <button type="submit" disabled={transferLoading} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium shadow-sm">
                  {transferLoading ? 'Processing...' : 'Mark as Transferred'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentDetail;
